import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Check, Mail, Lock } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import SignInPrompt from "../components/SignInPrompt";
import { subjectOptions } from "../data/content";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { user, loading: authLoading } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  // Pre-fill from signed-in user
  useEffect(() => {
    if (user) {
      setName((prev) => prev || user.name || "");
      setEmail((prev) => prev || user.email || "");
    }
  }, [user]);

  const toggleSubject = (s) => {
    setSubjects((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const validate = () => {
    if (!name.trim()) return "Please share your name.";
    if (!/^\S+@\S+\.\S+$/.test(email)) return "A valid email helps me reply.";
    if (subjects.length === 0) return "Pick at least one topic.";
    if (!message.trim()) return "The message is empty — please add a few lines.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setShowSignIn(true);
      return;
    }
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/contact`,
        {
          name: name.trim(),
          email: email.trim(),
          subjects,
          message: message.trim(),
        },
        { withCredentials: true }
      );
      setDone(true);
      toast.success(
        data.delivered
          ? "Sent — thank you for writing. A confirmation is on its way to your inbox."
          : "Saved — I will read this soon."
      );
    } catch (e2) {
      if (e2?.response?.status === 401) {
        setShowSignIn(true);
        return;
      }
      const detail =
        e2?.response?.data?.detail || "Something interrupted the send. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Could not send.");
    } finally {
      setSubmitting(false);
    }
  };

  // Wrapper handler attached to the BUTTON's onClick so even when disabled visually we can trigger
  const handleButtonClick = (e) => {
    if (!user) {
      e.preventDefault();
      setShowSignIn(true);
    }
  };

  const isLocked = !authLoading && !user;

  return (
    <div data-testid="page-contact">
      <PageHeader
        eyebrow="Write to me"
        title="A short note is always welcome."
        intro="Tell me what you read, where you walked, or which song you'd like me to try next. Pick one or more topics, and I will read every word — slowly, with a cup of tea."
        testid="contact-header"
      />

      <section className="pb-24">
        <div className="container-narrow max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] p-7 sm:p-12"
          >
            {done ? (
              <div className="text-center py-10" data-testid="contact-success">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#4A6B53]/15 text-[#4A6B53] mb-5">
                  <Check size={26} />
                </div>
                <h3 className="font-serif text-3xl text-[#2C2A28]">Thank you.</h3>
                <p className="mt-3 text-[#5C5852] max-w-md mx-auto">
                  Your note has been received. I usually reply within a few days —
                  always personally, never with a template.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setDone(false);
                    setSubjects([]);
                    setMessage("");
                  }}
                  className="mt-7 text-sm tracking-wide text-[#4A6B53] hover:text-[#3A5441] underline underline-offset-4"
                  data-testid="contact-send-another"
                >
                  Send another note
                </button>
              </div>
            ) : (
              <>
                {isLocked && (
                  <div
                    className="mb-6 flex items-start gap-3 p-4 rounded-sm bg-[#FAF9F6] border border-dashed border-[#CC7A52]/40"
                    data-testid="contact-auth-notice"
                  >
                    <Lock size={16} className="text-[#CC7A52] mt-0.5 shrink-0" />
                    <div className="text-sm text-[#5C5852] leading-relaxed">
                      To keep the inbox a kind place, only verified visitors can send a
                      message.{" "}
                      <button
                        type="button"
                        onClick={() => setShowSignIn(true)}
                        className="underline underline-offset-2 text-[#4A6B53] hover:text-[#3A5441]"
                        data-testid="contact-signin-link"
                      >
                        Sign in with Google
                      </button>{" "}
                      to write a note.
                    </div>
                  </div>
                )}

                <form onSubmit={onSubmit} className="space-y-7" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="name"
                        className="block eyebrow !text-[10px] mb-2"
                      >
                        Your name
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Meera Iyer"
                        className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-4 py-3 text-[#2C2A28] placeholder-[#5C5852]/60 field-ring transition-colors"
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block eyebrow !text-[10px] mb-2"
                      >
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@somewhere.com"
                        className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-4 py-3 text-[#2C2A28] placeholder-[#5C5852]/60 field-ring transition-colors"
                        data-testid="contact-input-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block eyebrow !text-[10px] mb-3">
                      Subject — pick one or more
                    </label>
                    <div className="flex flex-wrap gap-2.5" data-testid="contact-subjects">
                      {subjectOptions.map((opt) => {
                        const active = subjects.includes(opt);
                        return (
                          <button
                            type="button"
                            key={opt}
                            onClick={() => toggleSubject(opt)}
                            data-testid={`contact-subject-${opt
                              .toLowerCase()
                              .replace(/\s+/g, "-")}`}
                            aria-pressed={active}
                            className={`px-4 py-2 rounded-sm text-sm tracking-wide border transition-all duration-300 ${
                              active
                                ? "bg-[#4A6B53] text-[#FAF9F6] border-[#4A6B53]"
                                : "bg-transparent text-[#2C2A28] border-[#D8D3CC] hover:border-[#4A6B53] hover:text-[#4A6B53]"
                            }`}
                          >
                            {active && (
                              <Check size={14} className="inline mr-1.5 -mt-0.5" />
                            )}
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    {subjects.length > 0 && (
                      <p className="mt-2 text-xs text-[#5C5852]">
                        Selected: {subjects.join(", ")}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block eyebrow !text-[10px] mb-2"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={7}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="A few lines is plenty…"
                      className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-4 py-3 text-[#2C2A28] placeholder-[#5C5852]/60 field-ring transition-colors resize-y"
                      data-testid="contact-input-message"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                    <p className="text-xs text-[#5C5852] inline-flex items-center gap-2">
                      <Mail size={13} className="text-[#4A6B53]" />
                      Your note travels straight to my inbox. I never share emails.
                    </p>
                    <button
                      type="submit"
                      onClick={handleButtonClick}
                      disabled={submitting}
                      aria-disabled={isLocked || submitting}
                      className={`inline-flex items-center justify-center gap-2 px-7 py-3 rounded-sm text-sm tracking-wide transition-colors duration-300 ${
                        isLocked
                          ? "bg-[#9A9690] text-[#FAF9F6] cursor-pointer hover:bg-[#7E7A74]"
                          : "bg-[#4A6B53] text-[#FAF9F6] hover:bg-[#3A5441] disabled:opacity-60 disabled:cursor-not-allowed"
                      }`}
                      data-testid="contact-submit"
                      title={isLocked ? "Sign in with Google to send" : ""}
                    >
                      {isLocked ? (
                        <>
                          <Lock size={14} />
                          Sign in to send
                        </>
                      ) : (
                        <>
                          {submitting ? "Sending…" : "Send note"}
                          {!submitting && <Send size={15} />}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </section>

      <SignInPrompt
        open={showSignIn}
        onClose={() => setShowSignIn(false)}
        title="Sign in to send your note"
        message="To keep this inbox a kind, low-noise place, only verified visitors can send a message. Sign in with Google — it takes a moment, and you'll also get a friendly confirmation by email."
      />
    </div>
  );
}
